import { Thermometer, Droplets, ScanLine, ClipboardCheck, Wind, BadgeCheck } from "lucide-react";

const TOOLS = [
    {
        icon: Thermometer,
        title: "Thermal Imaging",
        body: "Infrared cameras and thermal guns reveal exactly where heat is escaping — the cold spots your bills feel but you can't see.",
    },
    {
        icon: Droplets,
        title: "Moisture Meters",
        body: "Pin-type and pinless moisture meters confirm whether the issue is leaky insulation, vapor problems, or hidden water damage.",
    },
    {
        icon: Wind,
        title: "Blower Door Test",
        body: "We pressurize the home with a calibrated fan to measure exactly how leaky it is in CFM50 — the gold standard for diagnosing air infiltration.",
    },
    {
        icon: ScanLine,
        title: "Air-Sealing Audit",
        body: "We trace airflow at top plates, can lights, ducts, and rim joists so we seal the right gaps — not just the obvious ones.",
    },
    {
        icon: ClipboardCheck,
        title: "Right-Sized Scope",
        body: "The data drives the proposal. You only pay for the work your home actually needs — no upselling, no guesswork.",
    },
];

export const Diagnostics = () => {
    return (
        <section
            id="diagnostics"
            data-testid="diagnostics-section"
            className="bg-ink text-bone py-24 md:py-32 relative overflow-hidden border-y border-ink"
        >
            <div className="absolute inset-0 grain pointer-events-none opacity-40" />
            <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16 items-end reveal">
                    <div className="lg:col-span-7">
                        <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5">
                            <span className="h-px w-10 bg-ember inline-block align-middle mr-3" />
                            Our Process
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                            Hi-tech instruments.
                            <br />
                            <span className="text-ember">Diagnose it right the first time.</span>
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-bone/75 leading-relaxed text-lg">
                        We don&apos;t guess. Every job starts with thermal imaging, moisture
                        meters, a blower door test, and an air-sealing audit so the work we
                        quote is the work you actually need — backed by real data, not a hunch.
                    </p>
                </div>

                {/* BPI Certified credential bar */}
                <div
                    data-testid="bpi-credential-bar"
                    className="reveal mb-10 bg-ember text-ink p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-5 md:gap-7"
                >
                    <BadgeCheck size={42} strokeWidth={1.5} className="text-ink shrink-0" />
                    <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1">
                            Owner-Operator Credential
                        </div>
                        <div className="font-heading text-xl md:text-2xl font-bold leading-snug">
                            BPI Certified
                            <span className="font-medium text-ink/80">
                                {" "}
                                · Building Performance Institute
                            </span>
                        </div>
                        <p className="text-sm md:text-base mt-2 text-ink/85">
                            Our owner-operator is BPI certified — the national standard for home
                            energy professionals. That means the diagnosis you get is grounded in
                            building science, not sales scripts.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-bone/10 border border-bone/15">
                    {TOOLS.map((t, idx) => {
                        const Icon = t.icon;
                        return (
                            <div
                                key={t.title}
                                data-testid={`diagnostic-${t.title.toLowerCase().replace(/\s+/g, "-")}`}
                                className="bg-ink p-7 md:p-8 reveal flex flex-col gap-4"
                                style={{ transitionDelay: `${idx * 70}ms` }}
                            >
                                <div className="h-12 w-12 border border-ember/40 text-ember flex items-center justify-center">
                                    <Icon size={24} strokeWidth={1.5} />
                                </div>
                                <h3 className="font-heading text-lg md:text-xl font-bold leading-tight">
                                    {t.title}
                                </h3>
                                <p className="text-sm text-bone/70 leading-relaxed">{t.body}</p>
                            </div>
                        );
                    })}
                </div>

                <p
                    data-testid="diagnostics-footnote"
                    className="text-sm text-bone/60 mt-10 max-w-2xl reveal"
                >
                    Same instruments the energy auditors use — included on every quote, no extra
                    charge.
                </p>
            </div>
        </section>
    );
};
